import {FC, useState, useEffect, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {getTaskReminders, snoozeContactTask, resolveContactTask} from './_requests'
import type {ReminderTask} from './_requests'

// Floating reminders for payment tasks, on every admin page (next to the
// meetings / tasks bubbles). Polls the server for tasks whose reminder time has
// come, pops a browser notification once per task per reminder, and lets the
// admin complete or snooze straight from the panel. Hidden while nothing is due.

const POLL_MS = 60000
const NOTIFIED_KEY = 'payment-task-notified' // {"<taskId>|<remind_at>": epoch ms}
const TZ = 'Asia/Jerusalem'

const whenLabel = (t: ReminderTask) => {
  const due = new Date(t.due_at)
  const day = due.toLocaleDateString('en-CA', {timeZone: TZ})
  const today = new Date().toLocaleDateString('en-CA', {timeZone: TZ})
  const [, m, d] = day.split('-').map(Number)
  const time = t.due_time || ''
  if (day === today) return time ? `היום ${time}` : 'היום'
  return `${d}/${m}${time ? ' ' + time : ''}`
}
const clientName = (t: ReminderTask) =>
  `${t.account?.first_name || ''} ${t.account?.last_name || ''}`.trim() || t.account?.company_name || 'לקוח'

// Remember what we already notified about, in this browser, so a reload or the
// next poll doesn't ring again. A snooze produces a new remind_at → rings again.
const readNotified = (): Record<string, number> => {
  try { return JSON.parse(window.localStorage.getItem(NOTIFIED_KEY) || '{}') || {} } catch (e) { return {} }
}
const writeNotified = (m: Record<string, number>) => {
  const cutoff = Date.now() - 14 * 86400000
  const pruned: Record<string, number> = {}
  Object.keys(m).forEach(k => { if (m[k] > cutoff) pruned[k] = m[k] })
  try { window.localStorage.setItem(NOTIFIED_KEY, JSON.stringify(pruned)) } catch (e) {}
}

const PaymentTasksBubble: FC = () => {
  const navigate = useNavigate()
  const [tasks, setTasks] = useState<ReminderTask[]>([])
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const notify = useCallback((list: ReminderTask[]) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    const seen = readNotified()
    let changed = false
    for (const t of list) {
      const key = `${t._id}|${t.snoozed_until || t.remind_at}`
      if (seen[key]) continue
      seen[key] = Date.now()
      changed = true
      try {
        const n = new Notification(t.overdue ? 'משימת תשלום באיחור' : 'תזכורת: משימת תשלום', {
          body: `${whenLabel(t)} · ${clientName(t)}${t.reason ? ' — ' + t.reason : ''}`,
          tag: `payment-task-${t._id}`,
          icon: '/favicon.ico',
        })
        n.onclick = () => { window.focus(); setOpen(true); n.close() }
      } catch (e) {}
    }
    if (changed) writeNotified(seen)
  }, [])

  const load = useCallback(async () => {
    try {
      const res = await getTaskReminders()
      const list = res.data.tasks || []
      setTasks(list)
      notify(list)
    } catch (e) {
      // Silently keep the last list — the bubble must never break a page.
    }
  }, [notify])

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {})
    }
    load()
    const timer = window.setInterval(load, POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible) }
  }, [load])

  const act = async (t: ReminderTask, fn: () => Promise<unknown>) => {
    setBusy(t._id)
    try {
      await fn()
      setTasks(ts => ts.filter(x => x._id !== t._id))
    } catch (e) {}
    setBusy(null)
    load()
  }

  if (tasks.length === 0) return null
  const overdue = tasks.filter(t => t.overdue).length
  const color = overdue ? '#f1416c' : '#ffa800'

  const small = (bg: string, fg: string, border = 'none'): React.CSSProperties => ({
    padding: '4px 8px', borderRadius: '6px', border, background: bg, color: fg,
    fontSize: '11px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  })

  return (
    <>
      <style>{`@keyframes ptaskPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.1)}}`}</style>
      <div
        onClick={() => setOpen(o => !o)}
        title="תזכורות משימות תשלום"
        style={{
          position: 'fixed', bottom: '208px', left: '24px', width: '48px', height: '48px', borderRadius: '50%',
          background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 1000,
          animation: overdue && !open ? 'ptaskPulse 1.6s ease-in-out infinite' : 'none',
        }}
      >
        <i className="bi bi-bell-fill" style={{fontSize: '20px', color: '#fff'}}></i>
        <div style={{
          position: 'absolute', top: '-4px', right: '-4px', minWidth: '20px', height: '20px', borderRadius: '10px', padding: '0 5px',
          background: '#fff', color, fontSize: '11px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        }}>{tasks.length}</div>
      </div>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{position: 'fixed', inset: 0, zIndex: 999}} />
          <div style={{
            position: 'fixed', bottom: '24px', left: '84px', width: '380px', maxWidth: 'calc(100vw - 100px)', maxHeight: '70vh',
            background: '#fff', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', zIndex: 1001,
            display: 'flex', flexDirection: 'column', direction: 'rtl', overflow: 'hidden',
          }}>
            <div style={{padding: '12px 16px', borderBottom: '1px solid #eff2f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <b style={{fontSize: '14px'}}><i className="bi bi-bell" style={{color, marginLeft: '6px'}}></i>משימות תשלום לטיפול ({tasks.length})</b>
              <button
                onClick={() => { setOpen(false); navigate('/admin/payments') }}
                style={{border: 'none', background: 'none', color: '#3699ff', fontSize: '12px', fontWeight: 700, cursor: 'pointer'}}
              >ללוח המשימות</button>
            </div>
            <div style={{overflowY: 'auto', padding: '8px 12px'}}>
              {tasks.map(t => (
                <div key={t._id} style={{
                  border: `1px solid ${t.overdue ? '#f8c4d0' : '#ffe8a3'}`, background: t.overdue ? '#fff5f8' : '#fffbeb',
                  borderRadius: '10px', padding: '8px 10px', marginBottom: '8px', opacity: busy === t._id ? 0.5 : 1,
                }}>
                  <div style={{display: 'flex', gap: '6px', alignItems: 'baseline', fontSize: '12px'}}>
                    <span style={{fontWeight: 800, color: t.overdue ? '#f1416c' : '#b58100', whiteSpace: 'nowrap'}}>
                      {t.overdue ? 'באיחור · ' : ''}{whenLabel(t)}
                    </span>
                    <span style={{fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{clientName(t)}</span>
                    {t.account?.phone && <a href={`tel:${t.account.phone}`} style={{fontSize: '11px', marginRight: 'auto', direction: 'ltr'}}>{t.account.phone}</a>}
                  </div>
                  {t.reason && <div style={{fontSize: '12px', color: '#3f4254', margin: '4px 0 6px', whiteSpace: 'pre-wrap'}}>{t.reason}</div>}
                  <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                    <button disabled={busy === t._id} style={small('#50cd89', '#fff')} onClick={() => act(t, () => resolveContactTask(t._id))}>
                      <i className="bi bi-check-lg"></i> הושלם
                    </button>
                    <button disabled={busy === t._id} style={small('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={() => act(t, () => snoozeContactTask(t._id, {minutes: 15}))}>עוד 15 דק׳</button>
                    <button disabled={busy === t._id} style={small('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={() => act(t, () => snoozeContactTask(t._id, {minutes: 60}))}>עוד שעה</button>
                    <button disabled={busy === t._id} style={small('#fff', '#7e8299', '1px solid #e4e6ef')} onClick={() => act(t, () => snoozeContactTask(t._id, {until: 'tomorrow'}))}>מחר 09:00</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default PaymentTasksBubble
